/**
 * Represents a countdown timer that displays the time remaining until a target day.
 * @class
 * @param {HTMLElement} block - The block element where the countdown timer will be displayed.
 * @param {Object} translation - The translations object that contains language-specific text.
 */
export class CountdownTimer {

	/**
	 * Initializes a new Constructor object.
	 *
	 * @param {HTMLElement} block - The block element to be associated with the Constructor.
	 * @param {Object} translation - The translations object containing language-specific data.
	 * @return {Constructor} - A new instance of the Constructor class.
	 */
	constructor( block, translation ) {

		this.block = block;
		this.translations = translation;
		this.lang = this.block.dataset.lang || 'en';
		this.interval = null;

		const targetDate = new Date( this.block.dataset.targetDay );
		if ( isNaN( targetDate.getTime() ) ) {

			this.block.innerHTML = this.translations[ this.lang ]?.finished ?? '';
			return;

		}
		this.targetDay = targetDate.getTime();
		this.init();

	}

	/**
	 * Pluralize function returns the correct form of a word based on the given number.
	 *
	 * @param {number} n - The number to determine which form of the word to return.
	 * @param {Array<string>} forms - An array of strings representing the different forms of the word.
	 *
	 * @return {string} The appropriate form of the word based on the provided number.
	 */
	pluralize( n, forms ) {

		if ( n === 1 ) {

			return forms[0];

		}
		if ( n % 10 >= 2 && n % 10 <= 4 && !( n % 100 >= 12 && n % 100 <= 14 ) ) {

			return forms[1];

		}
		return forms[2];

	}

	/**
	 * Update the text content of an element selected by the provided selector.
	 *
	 * @param {string} selector - The CSS selector used to identify the element to be updated.
	 * @param {string} value - The new text content to set for the element.
	 *
	 * @return {void}
	 */
	updateElement( selector, value ) {

		const el = this.block.querySelector( selector );
		if ( el ) {

			el.textContent = value;

		}

	}

	/**
	 * Update the countdown element based on the target date and current time.
	 * If the countdown has finished, display a "finished" message and clear the interval.
	 *
	 * @return {void}
	 */
	updateCountdown() {

		const now = new Date().getTime();
		const timeLeft = this.targetDay - now;
		const days = Math.floor( timeLeft / ( 1000 * 60 * 60 * 24 ) );
		const hours = Math.floor(
			( timeLeft % ( 1000 * 60 * 60 * 24 ) ) / ( 1000 * 60 * 60 )
		);
		const minutes = Math.floor( ( timeLeft % ( 1000 * 60 * 60 ) ) / ( 1000 * 60 ) );

		if  ( days <= 0 && hours <= 0 && minutes < 1 ) {

			this.block.innerHTML = this.translations[this.lang].finished;
			clearInterval( this.interval );
			return;

		}

		this.updateElement(
			'.js-countdown-days',
			days.toString().padStart( 2, '0' )
		);
		this.updateElement(
			'.js-countdown-days-label',
			this.pluralize( days, this.translations[this.lang].days )
		);
		this.updateElement(
			'.js-countdown-hours',
			hours.toString().padStart( 2, '0' )
		);
		this.updateElement(
			'.js-countdown-hours-label',
			this.pluralize( hours, this.translations[this.lang].hours )
		);
		this.updateElement(
			'.js-countdown-minutes',
			minutes.toString().padStart( 2, '0' )
		);
		this.updateElement(
			'.js-countdown-minutes-label',
			this.pluralize( minutes, this.translations[this.lang].minutes )
		);

	}

	/**
	 * Initializes the countdown timer by setting up an interval to update the countdown every second.
	 * The initial countdown is also updated immediately upon initialization.
	 *
	 * @return {void}
	 */
	init() {

		this.interval = setInterval( () => this.updateCountdown(), 1000 );
		this.updateCountdown();

	}

}

